import React, { forwardRef, type ReactNode, type RefObject } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { borderRadius, getControlBorder, getControlBorderColor, layoutGap } from '../../../theme/theme';
import ActionHelpTooltip from '../common/ActionHelpTooltip';
import SectionHeader from '../common/SectionHeader';
import type { HelpValue } from '../common/helpModel';

export type WorkspaceTone = 'neutral' | 'accent' | 'success' | 'warning' | 'error' | 'info';
export type WorkspaceActionTone = 'primary' | 'secondary' | 'quiet';

export interface WorkflowWorkspacePalette {
  shellBackground: string;
  paneBackground: string;
  paneBorder: string;
  paneDivider: string;
  sectionBackground: string;
  sectionRaisedBackground: string;
  sectionBorder: string;
  sectionAccentBackground: string;
  sectionAccentBorder: string;
  selectedBackground: string;
  selectedBorder: string;
  headingText: string;
  bodyText: string;
  mutedText: string;
  tertiaryText: string;
}

export const getWorkflowWorkspacePalette = (theme: Theme): WorkflowWorkspacePalette => ({
  shellBackground: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.5 : 0.2),
  paneBackground: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.72 : 0.34),
  paneBorder: alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.42 : 0.22),
  paneDivider: alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.34 : 0.18),
  sectionBackground: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.76 : 0.38),
  sectionRaisedBackground: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.9 : 0.46),
  sectionBorder: alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.38 : 0.22),
  sectionAccentBackground: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.035 : 0.065),
  sectionAccentBorder: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.18 : 0.24),
  selectedBackground: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.045 : 0.075),
  selectedBorder: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.24 : 0.34),
  headingText: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.94 : 0.96),
  bodyText: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.8 : 0.84),
  mutedText: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.68 : 0.72),
  tertiaryText: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.58 : 0.62),
});

/**
 * Shared chrome for top headers inside the mapping workspace tabs (Validation, Export,
 * Condition Studio, repair detail, etc.) so padding, border, and background stay aligned.
 */
export function getWorkspaceSubpageHeaderSurfaceSx(
  palette: WorkflowWorkspacePalette,
  options?: { sticky?: boolean; density?: 'standard' | 'compact' }
): SxProps<Theme> {
  const sticky = options?.sticky ?? true;
  const compact = options?.density === 'compact';
  return {
    ...(sticky
      ? {
          position: 'sticky' as const,
          top: 0,
          zIndex: 2,
        }
      : {}),
    px: compact ? { xs: 0.9, md: 1.2 } : { xs: 1.25, md: 1.75 },
    py: compact ? 0.4 : 1.25,
    borderBottom: `1px solid ${palette.paneBorder}`,
    backgroundColor: palette.sectionRaisedBackground,
    backdropFilter: 'blur(8px)',
  };
}

export function getWorkspaceIconButtonSx(
  theme: Theme,
  options?: { size?: number; tone?: WorkspaceTone; hoverAccent?: boolean }
): Record<string, any> {
  const size = options?.size ?? 28;
  const tone = options?.tone;
  const hoverAccent = options?.hoverAccent ?? false;
  const palette = getWorkflowWorkspacePalette(theme);
  const accentColor = tone ? getToneColor(theme, tone) : hoverAccent ? theme.palette.primary.main : undefined;

  return {
    width: size,
    height: size,
    borderRadius: `${borderRadius.sm}px`,
    border: getControlBorder(theme),
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.72 : 0.28),
    color: palette.bodyText,
    '&:hover': accentColor
      ? {
          backgroundColor: alpha(accentColor, theme.palette.mode === 'light' ? 0.06 : 0.1),
          borderColor: alpha(accentColor, theme.palette.mode === 'light' ? 0.5 : 0.6),
        }
      : {
          backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.92 : 0.38),
          borderColor: getControlBorderColor(theme),
        },
    '&.Mui-disabled': {
      borderColor: getControlBorderColor(theme),
      color: palette.tertiaryText,
    },
  };
}

export function getWorkspaceActionButtonSx(
  theme: Theme,
  options?: { tone?: WorkspaceActionTone; minHeight?: number }
): Record<string, any> {
  const tone = options?.tone ?? 'secondary';
  const minHeight = options?.minHeight ?? 30;
  const palette = getWorkflowWorkspacePalette(theme);

  return {
    minHeight,
    px: { xs: 0.8, sm: 1.05 },
    borderRadius: `${borderRadius.md}px`,
    borderColor: getControlBorderColor(theme),
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    '& .MuiButton-startIcon': {
      mr: layoutGap.dense,
      ml: 0,
    },
    '&:hover': {
      borderColor: getControlBorderColor(theme),
      boxShadow: 'none',
    },
    ...(tone === 'primary'
      ? {}
      : tone === 'quiet'
        ? {
            color: palette.mutedText,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.72 : 0.22),
              borderColor: getControlBorderColor(theme),
            },
          }
        : {
            color: palette.bodyText,
            backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.72 : 0.28),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.06 : 0.1),
              borderColor: getControlBorderColor(theme),
            },
          }),
  };
}

export function getWorkspaceCommandGroupSx(
  theme: Theme,
  options?: { density?: 'standard' | 'compact' }
): Record<string, any> {
  const compact = options?.density === 'compact';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 0,
    gap: compact ? layoutGap.micro : layoutGap.dense,
    px: 0,
    py: 0,
    borderRadius: `${borderRadius.md}px`,
    border: 'none',
    backgroundColor: 'transparent',
  };
}

export function getWorkspaceFooterSurfaceSx(theme: Theme): Record<string, any> {
  const palette = getWorkflowWorkspacePalette(theme);

  return {
    borderTop: `1px solid ${palette.paneBorder}`,
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.9 : 0.36),
    backdropFilter: 'blur(8px)',
    boxShadow: `0 -1px 0 ${alpha(theme.palette.common.black, 0.02)}`,
  };
}

export function getWorkspaceTableSurfaceSx(theme: Theme): Record<string, any> {
  const palette = getWorkflowWorkspacePalette(theme);

  return {
    borderRadius: `${borderRadius.md}px`,
    border: `1px solid ${palette.paneBorder}`,
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.72 : 0.26),
    boxShadow: 'none',
  };
}

export function getWorkspaceDrawerPaperSx(
  theme: Theme,
  side: 'left' | 'right' = 'right'
): Record<string, any> {
  const palette = getWorkflowWorkspacePalette(theme);
  const borderSide = side === 'right' ? 'borderLeft' : 'borderRight';
  const shadowX = side === 'right' ? -12 : 12;

  return {
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.98 : 0.94),
    backgroundImage: 'none',
    [borderSide]: `1px solid ${palette.paneBorder}`,
    boxShadow: `${shadowX}px 0 24px ${alpha(theme.palette.common.black, 0.26)}`,
  };
}

export type WorkspaceCompactPanelVariant =
  | 'default'
  | 'raised'
  | 'accent'
  | 'warning'
  | 'success'
  | 'primary';

export function getWorkspaceCompactPanelSx(
  theme: Theme,
  variant: WorkspaceCompactPanelVariant = 'default',
  options?: { padding?: 'none' | 'dense' | 'normal' }
): SystemStyleObject<Theme> {
  const padding = options?.padding ?? 'normal';
  const isLight = theme.palette.mode === 'light';

  const variantConfig = {
    default: {
      borderColor: alpha(theme.palette.divider, 0.1),
      backgroundColor: alpha(theme.palette.background.default, 0.14),
    },
    raised: {
      borderColor: alpha(theme.palette.divider, 0.12),
      backgroundColor: alpha(theme.palette.background.paper, 0.24),
    },
    accent: {
      borderColor: alpha(theme.palette.primary.main, 0.16),
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
    warning: {
      borderColor: alpha(theme.palette.warning.main, 0.22),
      backgroundColor: alpha(theme.palette.warning.main, 0.08),
    },
    success: {
      borderColor: alpha(theme.palette.success.main, 0.22),
      backgroundColor: alpha(theme.palette.success.main, 0.08),
    },
    primary: {
      borderColor: alpha(theme.palette.primary.main, 0.18),
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  }[variant];

  const paddingSx =
    padding === 'none'
      ? {}
      : padding === 'dense'
        ? { px: 0.75, py: 0.65 }
        : { px: 0.9, py: 0.8 };

  return {
    borderRadius: `${borderRadius.sm}px`,
    border: `1px solid ${variantConfig.borderColor}`,
    backgroundColor: variantConfig.backgroundColor,
    ...paddingSx,
  };
}

const getToneColor = (theme: Theme, tone: WorkspaceTone) => {
  switch (tone) {
    case 'accent':
      return theme.palette.primary.main;
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'error':
      return theme.palette.error.main;
    case 'info':
      return theme.palette.info.main;
    default:
      return theme.palette.text.secondary;
  }
};

export const WorkspacePane = forwardRef<HTMLDivElement, {
  children: ReactNode;
  sx?: SxProps<Theme>;
  raised?: boolean;
  dataTestId?: string;
}>(function WorkspacePane({
  children,
  sx,
  raised = false,
  dataTestId,
}, ref) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);

  return (
    <Box
      ref={ref}
      data-testid={dataTestId}
      sx={{
        width: '100%',
        minWidth: 0,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: raised ? palette.sectionRaisedBackground : palette.paneBackground,
        color: palette.bodyText,
        '& .MuiTypography-subtitle1, & .MuiTypography-subtitle2': {
          color: palette.headingText,
        },
        '& .MuiTypography-body1, & .MuiTypography-body2': {
          color: palette.bodyText,
        },
        '& .MuiTypography-caption': {
          color: palette.tertiaryText,
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
});

export function WorkspacePaneHeader({
  title,
  description,
  meta,
  actions,
  eyebrow,
  sx,
  dataTestId,
}: {
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
  sx?: SxProps<Theme>;
  dataTestId?: string;
}) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);
  const renderTypographyBlock = (
    value: ReactNode | undefined,
    variant: 'subtitle1' | 'body2' | 'caption',
    sxProp: SxProps<Theme>
  ) => {
    if (value === undefined || value === null) {
      return null;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return (
        <Typography variant={variant} sx={sxProp}>
          {value}
        </Typography>
      );
    }

    return <Box sx={sxProp}>{value}</Box>;
  };

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        px: 1.1,
        py: 0.95,
        borderBottom: `1px solid ${palette.paneDivider}`,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: layoutGap.row,
        ...sx,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={layoutGap.rise}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-start' }}
      >
        <Box sx={{ minWidth: 0 }}>
          {eyebrow ? (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: palette.tertiaryText,
                fontWeight: 700,
                letterSpacing: 0,
                textTransform: 'none',
                mb: layoutGap.micro,
              }}
            >
              {eyebrow}
            </Typography>
          ) : null}
          {renderTypographyBlock(title, 'subtitle1', {
            fontWeight: 700,
            lineHeight: 1.2,
            textTransform: 'none',
            color: palette.headingText,
          })}
          {renderTypographyBlock(description, 'body2', {
            mt: 0.35,
            color: palette.bodyText,
            lineHeight: 1.55,
            maxWidth: 720,
          })}
        </Box>
        {actions ? <Box sx={{ width: { xs: '100%', md: 'auto' } }}>{actions}</Box> : null}
      </Stack>
      {renderTypographyBlock(meta, 'caption', {
        color: palette.tertiaryText,
        lineHeight: 1.45,
        textTransform: 'none',
      })}
    </Box>
  );
}

export function WorkspaceSection({
  title,
  titleHelp,
  description,
  children,
  tone = 'neutral',
  sx,
  dataTestId,
}: {
  title?: ReactNode;
  titleHelp?: HelpValue;
  description?: ReactNode;
  children: ReactNode;
  tone?: WorkspaceTone;
  sx?: SxProps<Theme>;
  dataTestId?: string;
}) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);
  const toneColor = getToneColor(theme, tone);
  const useAccent = tone === 'accent';

  return (
    <Paper
      elevation={0}
      data-testid={dataTestId}
      sx={{
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${useAccent ? alpha(toneColor, 0.2) : palette.sectionBorder}`,
        backgroundColor: useAccent ? palette.sectionAccentBackground : palette.sectionBackground,
        px: 1.1,
        py: 1,
        minWidth: 0,
        ...sx,
      }}
    >
      {title ? (
        titleHelp ? (
          <SectionHeader
            label={typeof title === 'string' ? title : 'Section'}
            help={titleHelp}
            variant="body2"
            gutterBottom={false}
            sx={{
              fontWeight: 700,
              lineHeight: 1.35,
              color: useAccent ? palette.headingText : palette.bodyText,
            }}
          />
        ) : (
          <Typography
            data-testid="workspace-section-title"
            variant="body2"
            sx={{
              fontWeight: 700,
              lineHeight: 1.35,
              textTransform: 'none',
              letterSpacing: 0,
              color: useAccent ? palette.headingText : palette.bodyText,
            }}
          >
            {title}
          </Typography>
        )
      ) : null}
      {description ? (
        <Typography
          variant="body2"
          sx={{
            mt: title ? 0.3 : 0,
            color: palette.bodyText,
            lineHeight: 1.55,
          }}
        >
          {description}
        </Typography>
      ) : null}
      <Box sx={{ mt: title || description ? 0.75 : 0 }}>{children}</Box>
    </Paper>
  );
}

export function WorkspaceStatusPill({
  label,
  tone = 'neutral',
  size = 'default',
  uppercase = false,
  dataTestId,
}: {
  label: ReactNode;
  tone?: WorkspaceTone;
  size?: 'default' | 'compact';
  uppercase?: boolean;
  dataTestId?: string;
}) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);
  const toneColor = getToneColor(theme, tone);
  const compact = size === 'compact';

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? layoutGap.dense : layoutGap.row,
        minWidth: 0,
        px: compact ? 0.55 : 0.8,
        py: compact ? 0.22 : 0.38,
        borderRadius: `${borderRadius.full}px`,
        border: `1px solid ${tone === 'neutral' ? palette.sectionBorder : alpha(toneColor, 0.22)}`,
        backgroundColor: tone === 'neutral'
          ? alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.78 : 0.38)
          : alpha(toneColor, theme.palette.mode === 'light' ? 0.055 : 0.09),
      }}
    >
      <Box
        data-testid="workspace-pill-marker"
        sx={{
          width: compact ? 5 : 7,
          height: compact ? 5 : 7,
          borderRadius: `${borderRadius.full}px`,
          flexShrink: 0,
          backgroundColor: toneColor,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 700,
          lineHeight: 1.2,
          color: palette.bodyText,
          textTransform: uppercase ? 'uppercase' : 'none',
          letterSpacing: uppercase ? '0.04em' : 0,
          fontSize: compact ? '0.68rem' : 'inherit',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export function WorkspaceMetricGrid({
  children,
  minWidth = 160,
  dataTestId,
  sx,
}: {
  children: ReactNode;
  minWidth?: number;
  dataTestId?: string;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`,
        gap: layoutGap.row,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function WorkspaceMetricCard({
  label,
  value,
  supporting,
  tone = 'neutral',
  dataTestId,
}: {
  label: ReactNode;
  value: ReactNode;
  supporting?: ReactNode;
  tone?: WorkspaceTone;
  dataTestId?: string;
}) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);
  const toneColor = getToneColor(theme, tone);

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        minWidth: 0,
        px: 0.9,
        py: 0.8,
        borderRadius: `${borderRadius.sm}px`,
        border: `1px solid ${tone === 'neutral' ? palette.sectionBorder : alpha(toneColor, 0.18)}`,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.72 : 0.3),
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', color: palette.tertiaryText, textTransform: 'none' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ display: 'block', mt: 0.2, fontWeight: 700, lineHeight: 1.3, color: palette.headingText }}
      >
        {value}
      </Typography>
      {supporting ? (
        <Typography variant="caption" sx={{ display: 'block', mt: layoutGap.micro, color: palette.bodyText, lineHeight: 1.45 }}>
          {supporting}
        </Typography>
      ) : null}
    </Box>
  );
}

export function WorkspaceCallout({
  title,
  description,
  supporting,
  actions,
  tone = 'neutral',
  dataTestId,
}: {
  title: ReactNode;
  description?: ReactNode;
  supporting?: ReactNode;
  actions?: ReactNode;
  tone?: WorkspaceTone;
  dataTestId?: string;
}) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);
  const toneColor = getToneColor(theme, tone);
  const useAccent = tone === 'accent';

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'grid',
        gap: layoutGap.row,
        px: 1.05,
        py: 0.95,
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${useAccent ? alpha(toneColor, 0.22) : palette.sectionBorder}`,
        backgroundColor: useAccent
          ? alpha(toneColor, theme.palette.mode === 'light' ? 0.055 : 0.09)
          : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.72 : 0.34),
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3, textTransform: 'none', color: palette.headingText }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" sx={{ mt: 0.35, color: palette.bodyText, lineHeight: 1.55 }}>
            {description}
          </Typography>
        ) : null}
        {supporting ? (
          <Typography variant="body2" sx={{ mt: 0.35, color: palette.bodyText, lineHeight: 1.55 }}>
            {supporting}
          </Typography>
        ) : null}
      </Box>
      {actions}
    </Box>
  );
}

export function WorkspaceKeyValueList({
  items,
  columns = 'minmax(128px, 0.34fr) minmax(0, 1fr)',
  dataTestId,
  sx,
}: {
  items: Array<{ label: ReactNode; value: ReactNode; status?: 'ok' | 'warning' | 'error' }>;
  columns?: string;
  dataTestId?: string;
  sx?: SxProps<Theme>;
}) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);

  return (
    <Stack
      data-testid={dataTestId}
      spacing={layoutGap.cozy}
      sx={[
        {
          mt: 0.75,
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {items.map((entry, index) => (
        <Box
          key={`workspace-kv-${index}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: columns,
            gap: layoutGap.row,
            alignItems: 'start',
            py: 0.56,
            borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.18 : 0.12)}`,
            minWidth: 0,
            '&:last-child': {
              borderBottom: 'none',
            },
          }}
        >
          <Stack direction="row" spacing={layoutGap.dense} alignItems="center" sx={{ minWidth: 0 }}>
            {entry.status ? (
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  backgroundColor:
                    entry.status === 'ok'
                      ? theme.palette.success.main
                      : entry.status === 'warning'
                        ? theme.palette.warning.main
                        : theme.palette.error.main,
                  flexShrink: 0,
                }}
              />
            ) : null}
            <Typography
              variant="caption"
              sx={{
                minWidth: 0,
                overflowWrap: 'anywhere',
                color: palette.tertiaryText,
                fontSize: '0.76rem',
                lineHeight: 1.45,
              }}
            >
              {entry.label}
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              minWidth: 0,
              justifySelf: 'stretch',
              textAlign: 'left',
              fontWeight: 700,
              overflowWrap: 'anywhere',
              fontSize: '0.82rem',
              lineHeight: 1.45,
              color: palette.bodyText,
            }}
          >
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

export function WorkspaceBeforeAfterPreview({
  beforeLabel = 'Before',
  afterLabel = 'After',
  before,
  after,
  tone = 'neutral',
  dataTestId,
  sx,
}: {
  beforeLabel?: string;
  afterLabel?: string;
  before: { value: ReactNode; state?: ReactNode };
  after: { value: ReactNode; state?: ReactNode };
  tone?: WorkspaceTone;
  dataTestId?: string;
  sx?: SxProps<Theme>;
}) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);
  const toneColor = getToneColor(theme, tone);
  const useTone = tone !== 'neutral';

  const renderCard = (
    label: string,
    card: { value: ReactNode; state?: ReactNode },
    cardDataTestId?: string
  ) => (
    <Box
      data-testid={cardDataTestId}
      sx={{
        minWidth: 0,
        px: 0.95,
        py: 0.82,
        borderRadius: `${borderRadius.sm}px`,
        border: `1px solid ${useTone ? alpha(toneColor, theme.palette.mode === 'light' ? 0.22 : 0.28) : palette.sectionBorder}`,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.84 : 0.28),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: palette.tertiaryText,
          fontSize: '0.76rem',
          lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 0.18,
          minWidth: 0,
          overflowWrap: 'anywhere',
          fontWeight: 750,
          lineHeight: 1.4,
          fontSize: '0.86rem',
          color: palette.headingText,
        }}
      >
        {card.value}
      </Typography>
      {card.state ? (
        <Typography
          variant="caption"
          sx={{
            mt: 0.35,
            display: 'block',
            color: useTone ? toneColor : palette.bodyText,
            fontWeight: 700,
            lineHeight: 1.4,
          }}
        >
          {card.state}
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <Box
      data-testid={dataTestId}
      sx={[
        {
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: layoutGap.comfort,
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {renderCard(beforeLabel, before, dataTestId ? `${dataTestId}-before` : undefined)}
      {renderCard(afterLabel, after, dataTestId ? `${dataTestId}-after` : undefined)}
    </Box>
  );
}

export interface WorkspaceActionBarProps {
  actions: Array<{
    label: string;
    tone?: WorkspaceActionTone;
    onClick?: () => void;
    disabled?: boolean;
    startIcon?: ReactNode;
    buttonRef?: RefObject<HTMLButtonElement | null>;
    dataTestId?: string;
    help?: HelpValue;
    disabledHelp?: HelpValue;
  }>;
  dataTestId?: string;
  minActionWidth?: number;
  sx?: SxProps<Theme>;
}

export function WorkspaceActionBar({
  actions,
  dataTestId,
  minActionWidth = 148,
  sx,
}: WorkspaceActionBarProps) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${minActionWidth}px, 1fr))`,
        gap: layoutGap.row,
        width: '100%',
        ...sx,
      }}
    >
      {actions.map((action, index) => {
        const tone = action.tone ?? (index === 0 ? 'primary' : 'secondary');
        const variant = tone === 'primary' ? 'contained' : tone === 'secondary' ? 'outlined' : 'text';
        const buttonKey = `${action.label}-${index}`;
        const button = (
          <Button
            ref={action.buttonRef}
            data-testid={action.dataTestId ?? (tone === 'primary' ? 'workspace-action-primary' : undefined)}
            data-workspace-action-tone={tone}
            size="small"
            variant={variant}
            startIcon={action.startIcon}
            onClick={action.onClick}
            disabled={action.disabled}
            sx={{
              justifyContent: 'center',
              minWidth: 0,
              width: '100%',
              boxShadow: 'none',
              ...(tone === 'secondary'
                ? {
                    borderColor: getControlBorderColor(theme),
                    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.72 : 0.28),
                    color: palette.bodyText,
                  }
                : tone === 'quiet'
                  ? {
                      color: palette.mutedText,
                    }
                  : {}),
            }}
          >
            {action.label}
          </Button>
        );

        return (
          <ActionHelpTooltip
            key={buttonKey}
            help={action.help}
            disabledHelp={action.disabledHelp}
            disabled={action.disabled}
            fullWidth
          >
            {button}
          </ActionHelpTooltip>
        );
      })}
    </Box>
  );
}

export function WorkspaceNavRow({
  title,
  supporting,
  leading,
  meta,
  trailing,
  selected = false,
  onClick,
  tone = 'neutral',
  dataTestId,
  sx,
}: {
  title: ReactNode;
  supporting?: ReactNode;
  leading?: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  selected?: boolean;
  onClick?: () => void;
  tone?: WorkspaceTone;
  dataTestId?: string;
  sx?: SxProps<Theme>;
}) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);
  const toneColor = getToneColor(theme, tone);

  return (
    <ButtonBase
      data-testid={dataTestId}
      onClick={onClick}
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        textAlign: 'left',
        px: 0.8,
        py: 0.72,
        borderRadius: `${borderRadius.sm}px`,
        border: `1px solid ${selected ? alpha(toneColor, 0.24) : 'transparent'}`,
        backgroundColor: selected ? alpha(toneColor, theme.palette.mode === 'light' ? 0.065 : 0.1) : 'transparent',
        boxShadow: selected ? `inset 2px 0 0 ${toneColor}` : 'none',
        transition: 'background-color 0.18s ease, border-color 0.18s ease',
        '&:hover': {
          backgroundColor: selected
            ? alpha(toneColor, theme.palette.mode === 'light' ? 0.085 : 0.12)
            : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.62 : 0.2),
        },
        ...sx,
      }}
    >
      <Stack direction="row" spacing={layoutGap.rise} sx={{ width: '100%', minWidth: 0 }} alignItems="flex-start">
        {leading ? <Box sx={{ mt: 0.08, color: selected ? toneColor : palette.tertiaryText }}>{leading}</Box> : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={layoutGap.steady} alignItems="center" justifyContent="space-between">
            <Typography
              variant="body2"
              sx={{ fontWeight: selected ? 700 : 600, minWidth: 0, lineHeight: 1.3, color: selected ? palette.headingText : palette.bodyText }}
            >
              {title}
            </Typography>
            {trailing}
          </Stack>
          {supporting ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.2, color: palette.bodyText, lineHeight: 1.45 }}>
              {supporting}
            </Typography>
          ) : null}
          {meta ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.2, color: palette.tertiaryText, lineHeight: 1.4 }}>
              {meta}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </ButtonBase>
  );
}
